import { MessageDescriptor } from 'react-intl'

import { ESSENTIALS_HOME_CACHE_KEY } from './constants'
import {
  ESSENTIALS_CONTENT_CATEGORY,
  ESSENTIALS_COURSE,
  ESSENTIALS_SITE_CATEGORY,
  EssentialsArticleRaw,
  EssentialsContentFormatted,
  EssentialsContentRaw,
  EssentialsContentSection,
  EssentialsCourseRaw,
  HomebaseEssentialsStorageContents,
  OnEssentialsCardPressFunction,
  OnEssentialsCardPressParams,
} from './types'
import {
  ESSENTIALS_CONTENT_ITEM_PROPERTIES,
  HOMEBASE_ESSENTIALS_CAROUSEL_CONTENT_PROPERTIES,
} from '../common/constants/mixpanelConstants'
import { getEssentialsCmsAxiosInstance } from '../common/utils/commonUtils'
import { translateToEssentialSiteCode } from '../pathways/utils'

export const runEssentialsQuery = async ({
  locale,
  slug,
  query,
  essentialsCmsUrl,
  logError,
  isSingleResult,
  url,
  siteCategory = ESSENTIALS_SITE_CATEGORY.ESSENTIALS,
}: {
  locale: string
  slug: string
  query: string
  essentialsCmsUrl: string
  logError?: (error: Error) => void
  isSingleResult?: boolean
  url?: string
  siteCategory?: ESSENTIALS_SITE_CATEGORY
}) => {
  const payload = {
    query,
    variables: {
      slug,
      siteCategory,
      site: translateToEssentialSiteCode(locale),
    },
  }

  try {
    if (!essentialsCmsUrl) {
      throw new Error('CMS URL is empty')
    }

    const instance = getEssentialsCmsAxiosInstance(essentialsCmsUrl)
    const response = await instance.post(url ?? '', payload)

    if (response.status !== 200) {
      throw new Error('Invalid response for essentials query')
    } else if (isSingleResult) {
      return response.data?.data?.entry
    } else {
      return response.data?.data?.entries
    }
  } catch (e) {
    console.error('Failed to retrieve data for essentials query ' + e)
    if (logError) {
      logError(e as Error)
    }
    return []
  }
}

export const formatEssentialsArticle = (
  data: EssentialsArticleRaw,
  onEssentialsCardPress: OnEssentialsCardPressFunction,
  locale: string,
) => {
  const uriArr = data.uri.split('/')
  const destinationSlug = uriArr.length > 0 ? uriArr[uriArr.length - 1] : ''
  const contentTopics: string[] = []
  const contentSubtopics: string[] = []
  const contentTools: string[] = []
  const contentSubtools: string[] = []

  const parseContentType = (contentType: string) => {
    return contentType.split('_').join(' ')
  }

  if (data?.category) {
    data.category.forEach((category) => {
      if (category.type === 'topic') {
        if (category.parent) {
          contentSubtopics.push(category.title)
          contentTopics.push(category.parent.title)
        } else {
          contentTopics.push(category.title)
        }
      } else if (category.type === 'tool') {
        if (category.parent) {
          contentSubtools.push(category.title)
          contentTools.push(category.parent.title)
        } else {
          contentTools.push(category.title)
        }
      }
    })
  }
  const essentialContent: EssentialsContentFormatted = {
    title: data.title,
    imageUrl: data.image && data.image.length > 0 ? `https:${data.image[0].url}` : '',
    contentType: parseContentType(data.contentType),
    id: data.id,
    readTime: data.readTime,
    contentFormat: data.contentFormat,
    onPress: ({ isLyraSelect, index }: Partial<OnEssentialsCardPressParams>) => {
      onEssentialsCardPress({
        title: data.title,
        subPath: data.uri,
        contentFormat: data.contentFormat,
        contentType: data.contentType,
        destinationSlug,
        locale,
        contentTopics,
        contentSubtopics,
        contentTools,
        contentSubtools,
        contentId: String(data.id),
        isLyraSelect,
        index,
        isEnglish: data.isEnglish,
      })
    },
    uri: data.uri,
  }
  return essentialContent
}

export const formatEssentialsCourse = (
  data: EssentialsCourseRaw,
  onEssentialsCardPress: OnEssentialsCardPressFunction,
  locale: string,
) => {
  const uriArr = data.uri.split('/')
  const destinationSlug = uriArr.length > 0 ? uriArr[uriArr.length - 1] : ''
  const essentialContent: EssentialsContentFormatted = {
    title: data.title,
    imageUrl: data.foreground && data.foreground.length > 0 ? `https:${data.foreground[0].url}` : '',
    contentType: '',
    id: data.id,
    readTime: `${data.subcategories.length} lessons`,
    contentFormat: '',
    categoryType: ESSENTIALS_CONTENT_CATEGORY.COURSE,
    onPress: () => {
      onEssentialsCardPress({
        title: data.title,
        subPath: data.uri,
        contentFormat: '',
        contentType: '',
        destinationSlug,
        locale,
        isEnglish: data.isEnglish,
      })
    },
    uri: data.uri,
  }
  return essentialContent
}

export const mergeEssentialsContents = (
  articles: EssentialsArticleRaw[],
  courses: EssentialsCourseRaw[],
  coursesExpectedIndex: Partial<Record<ESSENTIALS_COURSE, number>>,
): EssentialsContentRaw[] => {
  const result: EssentialsContentRaw[] = []
  courses.forEach((item) => {
    const index = coursesExpectedIndex[item.slug as ESSENTIALS_COURSE]
    if (index !== undefined) {
      result[index] = item
    }
  })
  const totalLength = articles.length + courses.length
  let articlesIndex = 0
  for (let i = 0; i < totalLength; i++) {
    if (!result[i]) {
      result[i] = articles[articlesIndex]
      articlesIndex++
    }
  }

  return result.filter((item) => item)
}

export const isEssentialsUriCoursesCategory = (uri: string) => {
  return uri.includes('courses/')
}

const translateContentRawToProp = (content: EssentialsContentRaw, index: number) => {
  return {
    [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_ID]: content.id,
    [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_TYPE]: isEssentialsUriCoursesCategory(content.uri)
      ? HOMEBASE_ESSENTIALS_CAROUSEL_CONTENT_PROPERTIES.COURSE
      : HOMEBASE_ESSENTIALS_CAROUSEL_CONTENT_PROPERTIES.ARTICLE,
    [ESSENTIALS_CONTENT_ITEM_PROPERTIES.ITEM_RANK]: index + 1,
  }
}

export const getHomebaseEssentialsCarouselMixpanelPropFromStorage = (essentialsContents: {
  [key: string]: HomebaseEssentialsStorageContents
}) => {
  const cachedContents = essentialsContents[ESSENTIALS_HOME_CACHE_KEY]
  if (cachedContents) {
    const contents = cachedContents.contents ?? []
    return contents.map((content, index) => translateContentRawToProp(content, index))
  }

  return []
}

export const buildEssentialsEventsQuery = ({
  includeTags = false,
}: {
  includeTags?: boolean
}) => `query getEvents($siteCategory: [String], $site: [String]${includeTags ? ', $tags: [String]' : ''}) {
  entries(
      relatedToCategories: {slug: $siteCategory},
      ${includeTags ? 'relatedToTags: {slug: $tags},' : ''}
      section: "events",
      limit: 100,
      site: $site
  ) {
      ...event
  }
}

fragment event on events_events_Entry {
  ...baseProps
  eventType
  eventTimes {
      eventDate
      eventId
  }
  eventSeries
  eventSeriesTimes {
      eventDate
      eventId
  }
  seatCount
  facilitators {
      facilitatorName
      photo {
          ...image
      }
  }
}

fragment image on AssetInterface {
  url
}

fragment baseProps on EntryInterface {
  id
  uri
  slug
  title
  tags
}`

export const reconstructEssentialsSections = (
  essentialsContents: EssentialsContentFormatted[],
  essentialsSections: { title: MessageDescriptor; articleIds: number[] }[],
): EssentialsContentSection[] => {
  const allContentMap = essentialsContents.reduce((map, item) => {
    if (!item.id) return map
    map[item.id] = item
    return map
  }, {} as { [key: number]: EssentialsContentFormatted })

  if (!essentialsContents.length) {
    return []
  }

  // Restore the original structure using the mapped content
  return essentialsSections.map((section) => {
    return {
      title: section.title,
      content: section.articleIds.map((id) => allContentMap[id]).filter(Boolean) as EssentialsContentFormatted[],
    }
  })
}

export const getEssentialsSubpath = ({
  slug,
  category,
  subPath,
}: {
  slug?: string
  category?: ESSENTIALS_CONTENT_CATEGORY
  subPath?: string
}) => {
  const categoryStr = `${category}s`
  const subPathStr = slug ? `/${categoryStr}/${slug}` : subPath ? `${subPath.startsWith('/') ? '' : '/'}${subPath}` : ''
  return subPathStr
}

export const getArticleIdToSectionTitleMap = (
  sections: Array<{ title: string; articleIds: number[] }>,
): Record<number, { sectionTitle: string }> => {
  const mapping: Record<number, { sectionTitle: string }> = {}

  sections.forEach((section) => {
    section.articleIds.forEach((articleId) => {
      mapping[articleId] = { sectionTitle: section.title }
    })
  })

  return mapping
}

export const isUngatedEventDetailURL = (url: string | undefined) => {
  return url?.match(/\/events\/[^/?]+/)
}
